import type {
  SharedRoomPrivateDocumentMetadata,
  SharedRoomSummary,
  SharedRoomView,
} from "@rakazo/contracts";
import { Button, Textarea } from "@rakazo/ui-web";
import { Label } from "@rakazo/ui-web/components/ui/label";
import { useEffect, useRef, useState } from "react";
import { roomClient, roomMutationKey } from "../../lib/shared-room-client";

export interface PrivateRoomSourceInfo {
  roomId: string;
  title: string;
  bindingId: string;
  botId: string;
  fromSeq: number;
  throughSeq: number;
  indexedThroughSeq: number;
  snapshotId?: string;
  pendingAttachments?: { id: string; state: string; error?: string }[];
}
export function readPrivateRoomSelection(
  params: URLSearchParams,
): { roomId: string; bindingId?: string } | null {
  const roomId = params.get("roomSource");
  const bindingId = params.get("roomBinding");
  if (!roomId || !/^[A-Za-z0-9_-]{1,128}$/.test(roomId) || params.getAll("roomSource").length !== 1)
    return null;
  if (
    bindingId !== null &&
    (!/^[A-Za-z0-9_-]{1,128}$/.test(bindingId) || params.getAll("roomBinding").length !== 1)
  )
    return null;
  return { roomId, ...(bindingId ? { bindingId } : {}) };
}

export function PrivateRoomSource({
  botId,
  roomId,
  bindingId,
  onRemove,
  onStatus,
}: {
  botId: string;
  roomId: string;
  bindingId?: string;
  onRemove: () => void;
  onStatus: (allowed: boolean) => void;
}) {
  const [source, setSource] = useState<PrivateRoomSourceInfo>();
  const [error, setError] = useState("");
  const statusRef = useRef(onStatus);
  statusRef.current = onStatus;
  useEffect(() => {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    statusRef.current(false);
    setSource(undefined);
    setError("");
    async function refresh() {
      try {
        const next = await roomClient.read<PrivateRoomSourceInfo>(
          `/api/rooms/${encodeURIComponent(roomId)}/private-context?botId=${encodeURIComponent(botId)}`,
          controller.signal,
        );
        if (
          next.botId !== botId ||
          next.roomId !== roomId ||
          (bindingId && next.bindingId !== bindingId)
        )
          throw new Error("Джерело не відповідає цьому боту.");
        if (!controller.signal.aborted) {
          setSource(next);
          setError("");
          statusRef.current(true);
        }
      } catch (reason) {
        if (!controller.signal.aborted) {
          setSource(undefined);
          setError(reason instanceof Error ? reason.message : "Кімнатне джерело недоступне.");
          statusRef.current(false);
        }
      } finally {
        if (!controller.signal.aborted) timer = setTimeout(() => void refresh(), 5000);
      }
    }
    void refresh();
    return () => {
      controller.abort();
      clearTimeout(timer);
      statusRef.current(false);
    };
  }, [botId, roomId, bindingId]);
  return (
    <section
      className="mx-4 mb-2 rounded-lg border border-border bg-card px-3 py-2 text-xs"
      aria-label="Кімнатне джерело приватного чату"
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          {source ? (
            <>
              <p className="font-medium">
                Джерело: {source.title}
                {source.snapshotId ? " · заморожений архів" : ""}
              </p>
              <p className="text-muted-foreground">
                #{source.fromSeq}–{source.throughSeq} · індексовано до #{source.indexedThroughSeq}.
                Приватна розмова не публікується.
              </p>
              {source.pendingAttachments?.length ? (
                <p role="status" className="text-warning">
                  Матеріалів з незавершеним аналізом: {source.pendingAttachments.length}
                </p>
              ) : null}
            </>
          ) : (
            <p role="status">{error || "Перевірка доступу до кімнатного джерела…"}</p>
          )}
        </div>
        <Button size="sm" variant="ghost" onClick={onRemove}>
          Прибрати джерело
        </Button>
      </div>
      {source ? <PrivateRoomDocuments botId={botId} /> : null}
    </section>
  );
}

type PrivateDocumentMetadata = SharedRoomPrivateDocumentMetadata;
interface PrivateDocumentView extends SharedRoomPrivateDocumentMetadata {
  content: string;
}
function PrivateRoomDocuments({ botId }: { botId: string }) {
  const [open, setOpen] = useState(false);
  const [documents, setDocuments] = useState<PrivateDocumentMetadata[]>([]);
  const [selected, setSelected] = useState<PrivateDocumentView>();
  const [error, setError] = useState("");
  const [acknowledge, setAcknowledge] = useState(false);
  const [pending, setPending] = useState(false);
  const previewRequest = useRef<AbortController | null>(null);
  useEffect(() => () => previewRequest.current?.abort(), []);
  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    void roomClient
      .read<PrivateDocumentMetadata[]>(
        `/api/private-room-documents?botId=${encodeURIComponent(botId)}`,
        controller.signal,
      )
      .then((value) => {
        if (!controller.signal.aborted) setDocuments(value);
      })
      .catch((reason: unknown) => {
        if (!controller.signal.aborted)
          setError(reason instanceof Error ? reason.message : "Документи недоступні.");
      });
    return () => controller.abort();
  }, [botId, open]);
  async function preview(id: string) {
    previewRequest.current?.abort();
    const controller = new AbortController();
    previewRequest.current = controller;
    setError("");
    setSelected(undefined);
    setAcknowledge(false);
    try {
      const next = await roomClient.read<PrivateDocumentView>(
        `/api/private-room-documents/${encodeURIComponent(id)}`,
        controller.signal,
      );
      if (!controller.signal.aborted) setSelected(next);
    } catch (reason) {
      if (!controller.signal.aborted)
        setError(reason instanceof Error ? reason.message : "Документ недоступний.");
    }
  }
  async function download() {
    if (!selected || !acknowledge || pending) return;
    setPending(true);
    setError("");
    try {
      const blob = await roomClient.download(
        `/api/private-room-documents/${encodeURIComponent(selected.id)}/export`,
        { acknowledgeIrrevocableCopy: true },
        roomMutationKey(),
      );
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "private-room-document.md";
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (reason) {
      setSelected(undefined);
      setError(reason instanceof Error ? reason.message : "Експорт недоступний.");
    } finally {
      setPending(false);
    }
  }
  return (
    <details className="mt-2" open={open} onToggle={(event) => setOpen(event.currentTarget.open)}>
      <summary className="cursor-pointer">Мої приватні документи з джерел кімнати</summary>
      <div className="mt-2 space-y-2">
        <ul className="space-y-1">
          {documents.map((item) => (
            <li key={item.id}>
              <button type="button" className="underline" onClick={() => void preview(item.id)}>
                {item.title}
              </button>
            </li>
          ))}
        </ul>
        {selected ? (
          <>
            <h3 className="font-medium">{selected.title}</h3>
            <pre className="max-h-72 overflow-auto whitespace-pre-wrap rounded border border-border p-2 text-xs">
              {selected.content}
            </pre>
            <Label className="flex items-start gap-2">
              <input
                type="checkbox"
                checked={acknowledge}
                onChange={(event) => setAcknowledge(event.target.checked)}
              />
              Розумію: завантажену копію неможливо відкликати після зміни доступу.
            </Label>
            <p className="text-xs text-muted-foreground">
              Перевірену експортовану копію можна вручну додати до нового приватного завдання.
              Експорт не запускає завдання, не дозволяє зовнішні дії та не змінює дозволи поточного
              запуску або місця призначення.
            </p>
            <Button size="sm" disabled={!acknowledge || pending} onClick={() => void download()}>
              Експортувати перевірену копію
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                previewRequest.current?.abort();
                setSelected(undefined);
              }}
            >
              Закрити документ
            </Button>
          </>
        ) : null}
        {error ? (
          <p role="alert" className="text-destructive">
            {error}
          </p>
        ) : null}
      </div>
    </details>
  );
}

/** Only this reviewed text and manually chosen files are published; never hidden private history. */
export function SharePrivateMessage({
  initialText,
  initialRoomId,
  onClose,
}: {
  initialText: string;
  initialRoomId?: string;
  onClose: () => void;
}) {
  const [rooms, setRooms] = useState<SharedRoomSummary[]>([]);
  const [roomId, setRoomId] = useState(initialRoomId ?? "");
  const [room, setRoom] = useState<SharedRoomView>();
  const [text, setText] = useState(initialText);
  const [files, setFiles] = useState<File[]>([]);
  const [accepted, setAccepted] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);
  const key = useRef(roomMutationKey());
  const sending = useRef(false);
  const uploaded = useRef<{ kind: "attachment"; id: string; version: number }[]>([]);
  useEffect(() => {
    const controller = new AbortController();
    void roomClient
      .list(controller.signal)
      .then((value) => {
        if (!controller.signal.aborted) setRooms(value);
      })
      .catch((reason: unknown) => {
        if (!controller.signal.aborted)
          setError(reason instanceof Error ? reason.message : "Кімнати недоступні.");
      });
    return () => controller.abort();
  }, []);
  useEffect(() => {
    setRoom(undefined);
    setAccepted(false);
    setPreviewing(false);
    uploaded.current = [];
    key.current = roomMutationKey();
    if (!roomId) return;
    const controller = new AbortController();
    void roomClient
      .room(roomId, controller.signal)
      .then((value) => {
        if (!controller.signal.aborted) setRoom(value);
      })
      .catch((reason: unknown) => {
        if (!controller.signal.aborted)
          setError(reason instanceof Error ? reason.message : "Кімната недоступна.");
      });
    return () => controller.abort();
  }, [roomId]);
  async function publish() {
    if (!room || !accepted || !previewing || sending.current || (!text.trim() && !files.length))
      return;
    sending.current = true;
    setPending(true);
    setError("");
    try {
      for (let index = uploaded.current.length; index < files.length; index++) {
        const item = await roomClient.upload(
          room.id,
          files[index]!,
          `${key.current}:file:${index}`,
        );
        uploaded.current.push({ kind: "attachment", id: item.id, version: item.version });
        if (["quarantined", "uploading", "rejected"].includes(item.state))
          throw new Error(
            "Файл прийнято на перевірку, але текст ще не опубліковано. Перевірте його стан у кімнаті та повторіть після завершення перевірки.",
          );
      }
      await roomClient.command(
        room.id,
        {
          type: "message.send",
          text,
          mentions: [],
          attachments: uploaded.current,
          intent: "question",
        },
        `${key.current}:message`,
      );
      onClose();
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Публікація не завершена. Перевірте стан файлів перед повтором.",
      );
    } finally {
      sending.current = false;
      setPending(false);
    }
  }
  return (
    <section
      className="mx-4 mb-3 max-h-[65vh] space-y-3 overflow-y-auto rounded-xl border border-border bg-card p-4"
      aria-label="Поділитися в кімнаті"
    >
      <header className="flex items-center justify-between gap-2">
        <h2 className="font-medium">Поділитися в кімнаті</h2>
        <Button variant="ghost" size="sm" disabled={pending} onClick={onClose}>
          Закрити
        </Button>
      </header>
      <Label className="block space-y-1 text-sm">
        Кімната
        <select
          value={roomId}
          disabled={pending}
          className="block w-full rounded border border-input bg-background p-2"
          onChange={(event) => setRoomId(event.target.value)}
        >
          <option value="">Оберіть кімнату</option>
          {rooms.map((item) => (
            <option key={item.id} value={item.id}>
              {item.title}
            </option>
          ))}
        </select>
      </Label>
      <Label className="block space-y-1 text-sm">
        Лише цей текст
        <Textarea
          rows={5}
          value={text}
          disabled={pending}
          onChange={(event) => {
            setText(event.target.value);
            setPreviewing(false);
            key.current = roomMutationKey();
          }}
        />
      </Label>
      <Label className="block space-y-1 text-sm">
        Окремо обрані файли
        <input
          type="file"
          multiple
          disabled={pending}
          onChange={(event) => {
            setFiles([...(event.target.files ?? [])]);
            uploaded.current = [];
            setPreviewing(false);
            key.current = roomMutationKey();
          }}
        />
      </Label>
      {previewing ? (
        <div className="space-y-2 rounded border border-border p-3 text-sm">
          <h3 className="font-medium">Перед публікацією: {room?.title}</h3>
          <p className="whitespace-pre-wrap">{text}</p>
          <ul>
            {files.map((file, index) => (
              <li key={`${file.name}:${index}`}>
                {file.name} · {Math.ceil(file.size / 1024)} КБ
              </li>
            ))}
          </ul>
          <Label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(event) => setAccepted(event.target.checked)}
            />
            <span>
              Дозволяю опублікувати саме цей текст і файли для нинішніх і майбутніх допущених
              учасників та їхніх ботів. Приватна історія й інші джерела не додаються.
            </span>
          </Label>
          <Button disabled={!accepted || pending} onClick={() => void publish()}>
            {pending ? "Публікація…" : "Опублікувати в кімнаті"}
          </Button>
        </div>
      ) : (
        <Button
          disabled={room?.state !== "active" || (!text.trim() && files.length === 0)}
          onClick={() => {
            setAccepted(false);
            setPreviewing(true);
          }}
        >
          Переглянути перед публікацією
        </Button>
      )}
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </section>
  );
}
